import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

/* ===============================
   TYPES
   =============================== */

type BadgeSelectSize    = 'sm' | 'md' | 'lg';
type BadgeSelectVariant = 'outline' | 'filled';

export interface BadgeSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface BadgeSelectProps {
  options:         BadgeSelectOption[];
  value?:          string;
  defaultValue?:   string;
  id?:             string;
  name?:           string;
  onChange?:       (value: string) => void;
  size?:           BadgeSelectSize;
  variant?:        BadgeSelectVariant;
  placeholder?:    string;
  disabled?:       boolean;
  className?:      string;
}

/* ===============================
   BASE STYLES
   =============================== */

const baseBtn = `
  inline-flex items-center gap-1.5
  font-['Inter'] font-medium
  rounded-lg border
  cursor-pointer whitespace-nowrap
  transition-all duration-150
  focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand
  disabled:opacity-50 disabled:cursor-not-allowed
`;

/* ===============================
   SIZES
   =============================== */

const sizeStyles: Record<BadgeSelectSize, string> = {
  sm: 'h-8  px-3   text-ui-sm  gap-1.5',
  md: 'h-10 px-3.5 text-ui-base gap-2',
  lg: 'h-12 px-4   text-body    gap-2',
};

const iconSizes: Record<BadgeSelectSize, string> = {
  sm: 'w-3.5 h-3.5',
  md: 'w-[18px] h-[18px]',
  lg: 'w-5 h-5',
};

const chevronSizes: Record<BadgeSelectSize, string> = {
  sm: 'w-3 h-3',
  md: 'w-3.5 h-3.5',
  lg: 'w-4 h-4',
};

/* ===============================
   VARIANTS
   =============================== */

const variantStyles: Record<BadgeSelectVariant, string> = {
  outline: `
    bg-surface-0
    text-primary
    border-strong
    hover:bg-surface-1
    dark:bg-surface-0
    dark:text-primary
    dark:border-strong
    dark:hover:bg-surface-hover
  `,
  filled: `
    bg-brand-light
    text-brand
    border-transparent
    hover:bg-brand-light/80
    dark:bg-brand-light
    dark:text-brand
    dark:border-transparent
  `,
};

const iconColorStyles: Record<BadgeSelectVariant, string> = {
  outline: 'text-secondary',
  filled:  'text-brand',
};

const chevronColorStyles: Record<BadgeSelectVariant, string> = {
  outline: 'text-tertiary',
  filled:  'text-brand opacity-70',
};

/* ===============================
   MENU STYLES
   =============================== */

const menuStyles = `
  absolute top-[calc(100%+6px)] left-0 z-50
  min-w-[172px]
  bg-surface-0
  border border-strong
  rounded-lg overflow-hidden
  shadow-md
`;

const optionBaseStyles = `
  flex items-center gap-2.5
  w-full px-3 py-2.5
  text-ui-sm text-left font-['Inter']
  text-primary
  border-b border-subtle
  last:border-b-0
  cursor-pointer
  transition-colors duration-100
  hover:bg-surface-1
  dark:hover:bg-surface-hover
`;

const optionActiveStyles = 'font-medium';

/* ===============================
   COMPONENT
   =============================== */

export const BadgeSelect = React.forwardRef<HTMLDivElement, BadgeSelectProps>(
  (
    {
      options      = [],
      value,
      defaultValue,
      id,
      name,
      onChange,
      size         = 'md',
      variant      = 'outline',
      placeholder  = 'Seleccionar',
      disabled     = false,
      className    = '',
    },
    ref
  ) => {
    const [open, setOpen]         = useState(false);
    const [selected, setSelected] = useState<string | undefined>(value ?? defaultValue);
    const containerRef = useRef<HTMLDivElement>(null);

    const activeValue  = value !== undefined ? value : selected;
    const activeOption = options.find(o => o.value === activeValue);

    useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
      };
      document.addEventListener('keydown', handler);
      return () => document.removeEventListener('keydown', handler);
    }, []);

    const handleSelect = (optionValue: string) => {
      if (value === undefined) setSelected(optionValue);
      onChange?.(optionValue);
      setOpen(false);
    };

    const btnClasses = [
      baseBtn,
      sizeStyles[size],
      variantStyles[variant],
      className,
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ');

    return (
      <div
        ref={(node) => {
          containerRef.current = node!;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className="relative inline-block"
      >
        <button
          id={id}
          name={name}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => !disabled && setOpen(prev => !prev)}
          className={btnClasses}
        >
          {activeOption?.icon && (
            <span className={`inline-flex items-center justify-center flex-shrink-0 [&>svg]:w-full [&>svg]:h-full ${iconSizes[size]} ${iconColorStyles[variant]}`}>
              {activeOption.icon}
            </span>
          )}

          <span className="leading-none">
            {activeOption?.label ?? placeholder}
          </span>

          <ChevronDown
            className={`
              flex-shrink-0 ml-0.5
              transition-transform duration-200
              ${chevronSizes[size]}
              ${chevronColorStyles[variant]}
              ${open ? 'rotate-180' : ''}
            `}
          />
        </button>

        {open && (
          <div className={menuStyles} role="listbox">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === activeValue}
                onClick={() => handleSelect(option.value)}
                className={[
                  optionBaseStyles,
                  option.value === activeValue ? optionActiveStyles : '',
                ]
                  .filter(Boolean)
                  .join(' ')
                  .replace(/\s+/g, ' ')}
              >
                {option.icon && (
                  <span className="inline-flex flex-shrink-0 w-4 h-4 text-secondary">
                    {option.icon}
                  </span>
                )}
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
);

BadgeSelect.displayName = 'BadgeSelect';


